import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FaTimes } from 'react-icons/fa';
import '../../styles/rt2-modal.css';

const SIZE_CLASS = {
  sm: 'rt-modal--sm',
  md: 'rt-modal--md',
  lg: 'rt-modal--lg',
  xl: 'rt-modal--xl',
};

/**
 * Shared premium modal shell for RobiTest.
 * Overlay + header / scrollable body / sticky footer.
 * Does not own form logic — compose children and footer actions.
 */
export function RtModal({
  open = true,
  onClose,
  title,
  subtitle,
  size = 'md',
  headerExtra = null,
  footer = null,
  children,
  className = '',
  bodyClassName = '',
  closeOnBackdrop = true,
  closeOnEscape = true,
  showClose = true,
  closeDisabled = false,
  lockScroll = true,
  labelledBy,
  describedBy,
  role = 'dialog',
  variant = 'default', // default | confirm | destructive
}) {
  const titleId = useId();
  const panelRef = useRef(null);
  const previousFocusRef = useRef(null);
  const resolvedLabelledBy = labelledBy || (title ? titleId : undefined);

  useEffect(() => {
    if (!open || !lockScroll) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open, lockScroll]);

  useEffect(() => {
    if (!open) return undefined;
    previousFocusRef.current = document.activeElement;
    const t = window.setTimeout(() => {
      const focusable = panelRef.current?.querySelector(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      focusable?.focus?.();
    }, 0);
    return () => {
      window.clearTimeout(t);
      const prev = previousFocusRef.current;
      if (prev && typeof prev.focus === 'function') {
        try {
          prev.focus();
        } catch {
          /* ignore */
        }
      }
    };
  }, [open]);

  useEffect(() => {
    if (!open || !closeOnEscape || typeof onClose !== 'function') return undefined;
    const onKeyDown = (event) => {
      if (event.key !== 'Escape' && event.key !== 'Esc') return;
      if (event.defaultPrevented) return;
      if (closeDisabled) return;
      event.preventDefault();
      onClose();
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [open, closeOnEscape, closeDisabled, onClose]);

  if (!open) return null;

  const handleBackdrop = (event) => {
    if (event.target !== event.currentTarget) return;
    if (!closeOnBackdrop || closeDisabled || typeof onClose !== 'function') return;
    onClose();
  };

  const sizeClass = SIZE_CLASS[size] || SIZE_CLASS.md;
  const variantClass = variant !== 'default' ? `rt-modal--${variant}` : '';

  const node = (
    <div
      data-rt-modal-overlay
      className="rt-modal-overlay"
      role="presentation"
      onClick={handleBackdrop}
    >
      <div
        ref={panelRef}
        className={`rt-modal ${sizeClass} ${variantClass} ${className}`.trim()}
        role={role}
        aria-modal="true"
        aria-labelledby={resolvedLabelledBy}
        aria-describedby={describedBy}
        onClick={(e) => e.stopPropagation()}
      >
        {(title || showClose || headerExtra) && (
          <header className="rt-modal__header">
            <div className="rt-modal__header-text">
              {title ? (
                <h2 id={titleId} className="rt-modal__title">
                  {title}
                </h2>
              ) : null}
              {subtitle ? <p className="rt-modal__subtitle">{subtitle}</p> : null}
              {headerExtra}
            </div>
            {showClose ? (
              <button
                type="button"
                className="rt-modal__close"
                onClick={onClose}
                disabled={closeDisabled}
                aria-label="Close"
                data-rt-modal-close
              >
                <FaTimes aria-hidden />
              </button>
            ) : null}
          </header>
        )}

        <div className={`rt-modal__body ${bodyClassName}`.trim()}>{children}</div>

        {footer != null ? <footer className="rt-modal__footer">{footer}</footer> : null}
      </div>
    </div>
  );

  if (typeof document === 'undefined') return node;
  return createPortal(node, document.body);
}

export function RtModalSection({ title, children, className = '' }) {
  return (
    <section className={`rt-modal__section ${className}`.trim()}>
      {title ? <h3 className="rt-modal__section-title">{title}</h3> : null}
      {children}
    </section>
  );
}

export function RtModalField({ label, htmlFor, required, hint, error, children, className = '' }) {
  return (
    <div className={`rt-modal__field ${className}`.trim()}>
      {label ? (
        <label htmlFor={htmlFor} className="rt-modal__label">
          {label}
          {required ? <span className="rt-modal__req"> *</span> : null}
        </label>
      ) : null}
      {children}
      {error ? <p className="rt-modal__error">{error}</p> : null}
      {!error && hint ? <p className="rt-modal__hint">{hint}</p> : null}
    </div>
  );
}

export function RtModalActions({ children, className = '' }) {
  return <div className={`rt-modal__actions ${className}`.trim()}>{children}</div>;
}

export default RtModal;
